import React, { useState, useRef, useEffect } from 'react';
import { Input, Tree, Button, Typography, Spin, Dropdown, message } from 'antd';
import { useEmbedMode } from '../hooks/useEmbedMode';
import {
  SearchOutlined,
  PlusOutlined,
  FileTextOutlined,
  MoreOutlined,
  EditOutlined,
  DragOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { OntologyNode } from '../hooks/useResourceTree';
import { ResourceHelpers } from '../utils/resourceHelpers';
import { FolderIcon, OntologyIcon } from './CustomIcons';
import {
  createNewNode,
  addNodeToCorrectPosition,
  addNodeToDefaultOrRoot,
  determineParentId,
  removeNodeFromCorrectPosition,
  findNodeRecursively,
  getDefaultFolderId,
  findNodeInfo,
} from '../utils/resourceTreeUtils';
import {
  RESOURCE_NAME_MAX_LENGTH,
  RESOURCE_TITLE_DISPLAY_MAX_LENGTH,
} from '../constants/resource';

const { Title, Text } = Typography;

interface ResourceTreeProps {
  ontologyList: OntologyNode[];
  setOntologyList: React.Dispatch<React.SetStateAction<OntologyNode[]>>;
  loading: boolean;
  selectedNode: OntologyNode | null;
  onSelect: (selectedKeys: React.Key[], info: any) => void;
  onReload: () => Promise<void>;
  onShowRenameModal: (nodeData: OntologyNode) => void;
  onShowMoveModal: (nodeData: OntologyNode) => void;
  onShowDeleteModal: (nodeData: OntologyNode) => void;
  onAddOntology?: () => void; // 添加可选的添加本体函数
  onAddFolder?: () => void; // 添加可选的添加文件夹函数
  editingKey?: string | null; // 外部传入的编辑key
  editingValue?: string; // 外部传入的编辑值
  setEditingKey?: (key: string | null) => void; // 设置编辑key的函数
  setEditingValue?: (value: string) => void; // 设置编辑值的函数
  onConfirmEdit?: (key: string, newTitle: string) => Promise<void>; // 确认编辑的函数
  onCancelEdit?: (key: string) => void; // 取消编辑的函数
  selectedKeys?: React.Key[]; // 当前选中的keys
  setSelectedKeys?: (keys: React.Key[]) => void; // 设置选中keys的函数
  onMoveResource?: (sourceId: number, targetId: number) => Promise<boolean>; // 移动资源的函数
  onDeleteResource?: (nodeData: OntologyNode) => Promise<boolean>; // 删除资源的函数
  expandedKeys?: React.Key[]; // 展开的节点keys
  setExpandedKeys?: (keys: React.Key[]) => void; // 设置展开keys的函数
}

export const ResourceTree: React.FC<ResourceTreeProps> = ({
  ontologyList,
  setOntologyList,
  loading,
  selectedNode,
  onSelect,
  onReload,
  onShowRenameModal,
  onShowMoveModal,
  onShowDeleteModal,
  onAddOntology,
  onAddFolder,
  editingKey: externalEditingKey,
  editingValue: externalEditingValue,
  setEditingKey: externalSetEditingKey,
  setEditingValue: externalSetEditingValue,
  onConfirmEdit,
  onCancelEdit,
  selectedKeys = [],
  setSelectedKeys,
  onMoveResource,
  onDeleteResource,
  expandedKeys = [],
  setExpandedKeys,
}) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [dragOverDelete, setDragOverDelete] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentDragNode, setCurrentDragNode] = useState<OntologyNode | null>(
    null
  );
  // 记录中文输入法组合态，避免误触发确认/取消导致输入中断
  const isComposingRef = useRef(false);
  // 输入值使用 ref 保存，避免组合输入时频繁触发重渲染导致输入法中断
  const editingValueRef = useRef('');

  // 使用外部传入的编辑状态，如果没有则使用内部状态
  const [internalEditingKey, setInternalEditingKey] = useState<string | null>(
    null
  );
  const [internalEditingValue, setInternalEditingValue] = useState('');

  const editingKey =
    externalEditingKey !== undefined ? externalEditingKey : internalEditingKey;
  const editingValue =
    externalEditingValue !== undefined
      ? externalEditingValue
      : internalEditingValue;
  const setEditingKey = externalSetEditingKey || setInternalEditingKey;
  const setEditingValue = externalSetEditingValue || setInternalEditingValue;

  const validateResourceName = (name: string): string | null => {
    if (!name) {
      return '名称不能为空';
    }
    if (name.length > RESOURCE_NAME_MAX_LENGTH) {
      return `名称不能超过${RESOURCE_NAME_MAX_LENGTH}个字`;
    }
    return null;
  };

  // 切换编辑节点时同步初始值
  useEffect(() => {
    editingValueRef.current = editingValue;
  }, [editingKey]);

  // 处理拖拽移动
  const handleDrop = async (info: any) => {
    const { dragNode, node, dropPosition, dropToGap } = info;

    // 结束拖拽状态
    setIsDragging(false);
    setDragOverDelete(false);

    // 获取拖拽节点和目标节点的数据
    const dragNodeData = dragNode.data as OntologyNode;
    const dropNodeData = node.data as OntologyNode;

    // 不允许拖拽正在编辑的节点
    if (dragNodeData.isEditing) {
      return;
    }

    // 不允许拖拽默认文件夹
    if (dragNodeData.title === 'Default') {
      message.warning('默认文件夹不能被移动');
      return;
    }

    // 不允许拖拽到自己或自己的子节点
    if (dragNodeData.key === dropNodeData.key) {
      return;
    }

    // 检查是否拖拽到自己的子节点
    const isDescendant = (
      parent: OntologyNode,
      child: OntologyNode
    ): boolean => {
      if (!parent.children) return false;
      for (const childNode of parent.children) {
        if (childNode.key === child.key) return true;
        if (isDescendant(childNode, child)) return true;
      }
      return false;
    };
    if (isDescendant(dragNodeData, dropNodeData)) {
      message.warning('不能移动到自己的子节点');
      return;
    }

    // 确定目标父目录ID和信息
    let targetParentId: number | undefined;
    let targetParentTitle: string = '';

    if (dropToGap) {
      // 拖拽到节点之间，与目标节点同级
      const nodeInfo = findNodeInfo(ontologyList, dropNodeData.key);
      if (nodeInfo?.parentId) {
        targetParentId = nodeInfo.parentId;
        targetParentTitle = nodeInfo.parentTitle || '';
      } else {
        // 根级别，文件夹可以移动到根目录，本体移动到默认文件夹
        if (dragNodeData.type === 'folder') {
          targetParentId = undefined; // 文件夹可以在根目录
          targetParentTitle = '根目录';
        } else {
          targetParentId = getDefaultFolderId(ontologyList);
          targetParentTitle = 'Default';
        }
      }
    } else {
      // 拖拽到节点上
      if (dropNodeData.type === 'folder') {
        // 拖拽到文件夹（包括空文件夹）
        targetParentId = dropNodeData.id;
        targetParentTitle = dropNodeData.title;
      } else {
        // 拖拽到本体，移动到本体所属的文件夹
        const nodeInfo = findNodeInfo(ontologyList, dropNodeData.key);
        if (nodeInfo?.parentId) {
          targetParentId = nodeInfo.parentId;
          targetParentTitle = nodeInfo.parentTitle || '';
        } else {
          // 根级别的本体，文件夹移动到根目录，本体移动到默认文件夹
          if (dragNodeData.type === 'folder') {
            targetParentId = undefined;
            targetParentTitle = '根目录';
          } else {
            targetParentId = getDefaultFolderId(ontologyList);
            targetParentTitle = 'Default';
          }
        }
      }
    }

    // 检查是否已经在目标位置
    if (targetParentId && dragNodeData.parentId === targetParentId) {
      return;
    }

    // 执行移动操作
    try {
      let success = false;

      if (onMoveResource && dragNodeData.id && targetParentId !== undefined) {
        // 使用外部传入的移动函数
        success = await onMoveResource(dragNodeData.id, targetParentId);
      } else if (dragNodeData.id) {
        // 使用默认的ResourceHelpers
        success = await ResourceHelpers.moveResource(
          dragNodeData.id,
          targetParentId || 0
        );
      }

      if (success) {
        // 显示详细的移动成功信息
        message.success(`"${dragNodeData.title}" 已移动到${targetParentTitle}`);
        await onReload();
      }
    } catch (error: any) {
      console.error('拖拽移动失败:', error);
      message.error(`移动失败：${error.message || '未知错误'}`);
    }
  };

  // 处理删除区域的拖拽
  const handleDeleteDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverDelete(false);
    setIsDragging(false);

    console.log('🗑️ 拖拽删除触发');

    // 优先使用保存的拖拽节点
    let draggedNode = currentDragNode;

    // 如果没有保存的节点，尝试从dataTransfer获取
    if (!draggedNode) {
      const draggedNodeKey = e.dataTransfer.getData('text/plain');
      console.log('🔑 从dataTransfer获取的节点key:', draggedNodeKey);

      if (draggedNodeKey) {
        // 递归查找被拖拽的节点
        const findDraggedNode = (
          nodes: OntologyNode[]
        ): OntologyNode | null => {
          for (const node of nodes) {
            if (node.key === draggedNodeKey) {
              return node;
            }
            if (node.children) {
              const found = findDraggedNode(node.children);
              if (found) return found;
            }
          }
          return null;
        };

        draggedNode = findDraggedNode(ontologyList);
      }
    }

    console.log('🎯 找到的节点:', draggedNode);

    if (!draggedNode) {
      console.log('⚠️ 没有找到拖拽节点');
      setCurrentDragNode(null);
      return;
    }

    // 清除当前拖拽节点
    setCurrentDragNode(null);

    // 不允许删除默认文件夹
    if (draggedNode.title === 'Default') {
      message.warning('默认文件夹不能被删除');
      return;
    }

    // 不允许删除正在编辑的节点
    if (draggedNode.isEditing) {
      return;
    }

    console.log('🛡️ 开始删除操作:', draggedNode.title, 'ID:', draggedNode.id);

    try {
      if (onDeleteResource) {
        console.log('📞 使用外部删除函数');
        // 使用外部传入的删除函数，由外部处理删除流程（含弹窗确认、提示与刷新）
        await onDeleteResource(draggedNode);
        return;
      }

      let success = false;
      if (draggedNode.id) {
        console.log('🛠️ 使用ResourceHelpers删除');
        // 使用默认的ResourceHelpers删除，传入默认的targetDirId
        success = await ResourceHelpers.deleteResource(draggedNode, undefined);
      }

      console.log('✅ 删除结果:', success);

      if (success) {
        // ResourceHelpers.deleteResource 内部已经显示了成功消息，这里不再重复显示
        await onReload();
      } else {
        message.error('删除失败，请稍后重试');
      }
    } catch (error: any) {
      console.error('❌ 删除失败:', error);
      message.error(`删除失败：${error.message || '未知错误'}`);
    }
  };

  // 处理拖拽开始
  const handleDragStart = (info: any) => {
    const dragNodeData = info.node.data as OntologyNode;

    console.log('🚀 拖拽开始:', dragNodeData.title, 'key:', dragNodeData.key);

    // 不允许拖拽正在编辑的节点和默认文件夹
    if (dragNodeData.isEditing || dragNodeData.title === 'Default') {
      console.log(
        '⚠️ 拖拽被阻止:',
        dragNodeData.isEditing ? '正在编辑' : '默认文件夹'
      );
      return;
    }

    // 保存当前拖拽的节点
    setCurrentDragNode(dragNodeData);
    // 设置拖拽状态
    setIsDragging(true);
    console.log('✅ 设置拖拽状态为true，保存节点:', dragNodeData.title);

    // 将节点key存储到dataTransfer中，用于删除区域识别（备用方案）
    if (info.event && info.event.dataTransfer) {
      info.event.dataTransfer.setData('text/plain', dragNodeData.key);
      console.log('💾 存储节点key到dataTransfer:', dragNodeData.key);
    } else {
      console.log('⚠️ dataTransfer不可用');
    }
  };

  // 处理拖拽结束
  const handleDragEnd = () => {
    setIsDragging(false);
    setDragOverDelete(false);
    setCurrentDragNode(null);
    console.log('🏁 拖拽结束，清除状态');
  };

  const allowDrop = (info: any) => {
    const { dragNode, dropNode, dropPosition } = info;
    const dragNodeData = dragNode.data as OntologyNode;
    const dropNodeData = dropNode.data as OntologyNode;

    // 不允许拖拽正在编辑的节点
    if (dragNodeData.isEditing) {
      return false;
    }

    // 不允许拖拽默认文件夹
    if (dragNodeData.title === 'Default') {
      return false;
    }

    // 不允许拖拽到自己
    if (dragNodeData.key === dropNodeData.key) {
      return false;
    }

    // 不允许拖拽到自己的子节点
    const isDescendant = (
      parent: OntologyNode,
      child: OntologyNode
    ): boolean => {
      if (!parent.children) return false;
      for (const childNode of parent.children) {
        if (childNode.key === child.key) return true;
        if (isDescendant(childNode, child)) return true;
      }
      return false;
    };

    if (isDescendant(dragNodeData, dropNodeData)) {
      return false;
    }

    // 所有拖拽操作都允许，因为我们可以移动到文件夹或本体所属的文件夹
    return true;
  };

  // 查找目标节点所在的文件夹路径（包含祖先文件夹）
  const findFolderPathToNode = (
    nodes: OntologyNode[],
    targetKey: string,
    folderPath: React.Key[] = []
  ): React.Key[] | null => {
    for (const node of nodes) {
      const nextFolderPath =
        node.type === 'folder' ? [...folderPath, node.key] : folderPath;

      if (node.key === targetKey) {
        return nextFolderPath;
      }

      if (node.children) {
        const foundPath = findFolderPathToNode(
          node.children,
          targetKey,
          nextFolderPath
        );
        if (foundPath) {
          return foundPath;
        }
      }
    }
    return null;
  };

  // 新增前清理未完成的临时节点，避免连续点击产生多个空节点
  const clearPendingTemporaryNode = () => {
    let hasRemovedPendingNode = false;

    const removePendingNodes = (nodes: OntologyNode[]): OntologyNode[] => {
      return nodes.reduce((acc, node) => {
        const normalizedTitle = (node.title || '').trim();
        const isTemporaryNode =
          node.key.startsWith('folder-') || node.key.startsWith('ontology-');
        const isPendingEmptyNode =
          isTemporaryNode && node.isEditing && !normalizedTitle;

        if (isPendingEmptyNode) {
          hasRemovedPendingNode = true;
          return acc;
        }

        const nextNode = node.children
          ? { ...node, children: removePendingNodes(node.children) }
          : node;
        acc.push(nextNode);
        return acc;
      }, [] as OntologyNode[]);
    };

    setOntologyList(prev => {
      const cleanedNodes = removePendingNodes(prev);
      return hasRemovedPendingNode ? cleanedNodes : prev;
    });

    if (hasRemovedPendingNode) {
      setEditingKey(null);
      setEditingValue('');
    }
  };

  // 确保新增位置的父目录链路可见，避免树全部折叠时看不到输入框
  const ensureSelectedNodePathExpanded = () => {
    if (!selectedNode || !setExpandedKeys) {
      return;
    }

    const folderPath = findFolderPathToNode(ontologyList, selectedNode.key);
    if (!folderPath || folderPath.length === 0) {
      return;
    }

    const nextExpandedKeys = Array.from(
      new Set([...expandedKeys, ...folderPath])
    );
    setExpandedKeys(nextExpandedKeys);
  };

  // 添加新文件夹
  const handleAddFolder = () => {
    clearPendingTemporaryNode();
    // 始终使用默认逻辑，忽略外部传入的函数
    const newNode = createNewNode('folder', selectedNode, ontologyList);

    // 将新文件夹添加到正确的位置
    if (selectedNode) {
      // 有选中的节点，添加到正确位置
      setOntologyList(prev =>
        addNodeToCorrectPosition(prev, newNode, selectedNode, ontologyList)
      );
      // 确保父目录展开
      ensureSelectedNodePathExpanded();
    } else {
      // 没有选中节点，添加到根级别
      setOntologyList(prev => [...prev, newNode]);
    }

    setEditingKey(newNode.key);
    setEditingValue('');
  };

  // 添加新本体
  const handleAddOntology = () => {
    clearPendingTemporaryNode();
    // 始终使用默认逻辑，忽略外部传入的函数
    const newNode = createNewNode('ontology', selectedNode, ontologyList);

    // 将新本体添加到正确的位置
    // 使用工具函数处理添加逻辑
    if (selectedNode) {
      // 有选中的节点，添加到正确位置
      setOntologyList(prev =>
        addNodeToCorrectPosition(prev, newNode, selectedNode, ontologyList)
      );
      // 确保父目录展开
      ensureSelectedNodePathExpanded();
    } else {
      // 没有选中节点，添加到默认文件夹下或根级别
      setOntologyList(prev =>
        addNodeToDefaultOrRoot(prev, newNode, expandedKeys, setExpandedKeys)
      );
    }

    setEditingKey(newNode.key);
    setEditingValue('');
  };

  // 确认编辑
  const handleConfirmEdit = async (key: string, newTitle: string) => {
    const normalizedTitle = newTitle.trim();
    const isNewFolder = key.startsWith('folder-');
    const isNewOntology = key.startsWith('ontology-');

    if (onConfirmEdit) {
      const nameValidationError = validateResourceName(normalizedTitle);
      if (nameValidationError) {
        message.warning(nameValidationError);
        return;
      }
      // 如果传入了外部的确认编辑函数，则调用它
      await onConfirmEdit(key, normalizedTitle);
    } else {
      // 否则使用默认逻辑
      if (!normalizedTitle) {
        // 从正确的位置删除元素
        const removeFromCorrectPosition = (
          nodes: OntologyNode[]
        ): OntologyNode[] => {
          return nodes
            .map(node => {
              if (node.children) {
                return {
                  ...node,
                  children: removeFromCorrectPosition(node.children).filter(
                    child => child.key !== key
                  ),
                };
              }
              return node;
            })
            .filter(node => node.key !== key);
        };

        if (isNewFolder || isNewOntology) {
          setOntologyList(prev => removeFromCorrectPosition(prev));
        } else {
          message.warning('名称不能为空');
          return;
        }
      } else {
        const nameValidationError = validateResourceName(normalizedTitle);
        if (nameValidationError) {
          message.warning(nameValidationError);
          return;
        }

        if (isNewFolder) {
          const tempNode = findNodeRecursively(ontologyList, key);
          const parentId = tempNode?.parentId;
          console.log('📁 创建文件夹:', normalizedTitle, 'parentId:', parentId);

          try {
            await ResourceHelpers.createFolder(normalizedTitle, parentId);
            await onReload();
          } catch (error) {
            // 错误已在ResourceHelpers中处理
          }
          // 从正确的位置删除元素
          const removeFromCorrectPosition = (
            nodes: OntologyNode[]
          ): OntologyNode[] => {
            return nodes
              .map(node => {
                if (node.children) {
                  return {
                    ...node,
                    children: removeFromCorrectPosition(node.children).filter(
                      child => child.key !== key
                    ),
                  };
                }
                return node;
              })
              .filter(node => node.key !== key);
          };

          setOntologyList(prev => removeFromCorrectPosition(prev));
        } else if (isNewOntology) {
          const tempNode = findNodeRecursively(ontologyList, key);
          let parentId = tempNode?.parentId;

          // 如果没有指定父目录，使用默认文件夹
          if (!parentId) {
            parentId = getDefaultFolderId(ontologyList);
          }

          console.log('📋 创建本体:', normalizedTitle, 'parentId:', parentId);

          try {
            const newNode = await ResourceHelpers.createOntology(
              normalizedTitle,
              parentId
            );
            await onReload();
            // 跳转到OPM编辑器并传递新创建本体的ID
            navigate(`/opm-editor?id=${newNode.ontologyId || newNode.id}`);
          } catch (error) {
            // 错误已在ResourceHelpers中处理
          }
          // 从正确的位置删除元素
          const removeFromCorrectPosition = (
            nodes: OntologyNode[]
          ): OntologyNode[] => {
            return nodes
              .map(node => {
                if (node.children) {
                  return {
                    ...node,
                    children: removeFromCorrectPosition(node.children).filter(
                      child => child.key !== key
                    ),
                  };
                }
                return node;
              })
              .filter(node => node.key !== key);
          };

          setOntologyList(prev => removeFromCorrectPosition(prev));
        } else {
          setOntologyList(prev =>
            prev.map(item =>
              item.key === key
                ? { ...item, title: normalizedTitle, isEditing: false }
                : item
            )
          );
        }
      }
      setEditingKey(null);
      setEditingValue('');

      // 保留当前树选择，仅在选中项正好是当前编辑节点时移除它
      if (setSelectedKeys) {
        const nextSelectedKeys = selectedKeys.filter(
          selectedKey => selectedKey !== key
        );
        if (nextSelectedKeys.length !== selectedKeys.length) {
          setSelectedKeys(nextSelectedKeys);
        }
      }
    }
  };

  // 取消编辑
  const handleCancelEdit = (key: string) => {
    if (onCancelEdit) {
      // 如果传入了外部的取消编辑函数，则调用它
      onCancelEdit(key);
    } else {
      // 否则使用默认逻辑 - 从正确的位置删除元素
      setOntologyList(prev => removeNodeFromCorrectPosition(prev, key));
      setEditingKey(null);
      setEditingValue('');
    }
  };

  // 处理重命名确认
  const handleRenameConfirm = async (
    nodeData: OntologyNode,
    newName: string
  ) => {
    const normalizedName = newName.trim();
    const nameValidationError = validateResourceName(normalizedName);
    if (nameValidationError) {
      message.warning(nameValidationError);
      return false;
    }

    try {
      await ResourceHelpers.renameResource(nodeData, normalizedName);
      await onReload();
      return true;
    } catch (error: any) {
      console.error('重命名失败:', error);
      message.error(`重命名失败：${error.message || '未知错误'}`);
      return false;
    }
  };

  // 处理移动确认
  const handleMoveConfirm = async (sourceId: number, targetId: number) => {
    try {
      const success = await ResourceHelpers.moveResource(sourceId, targetId);
      if (success) {
        await onReload();
      }
      return success;
    } catch (error: any) {
      console.error('移动失败:', error);
      message.error(`移动失败：${error.message || '未知错误'}`);
      return false;
    }
  };

  // 处理删除确认
  const handleDeleteConfirm = async (
    nodeData: OntologyNode,
    targetDirId?: number
  ) => {
    try {
      const success = await ResourceHelpers.deleteResource(
        nodeData,
        targetDirId
      );
      if (success) {
        await onReload();
      }
      return success;
    } catch (error: any) {
      console.error('删除失败:', error);
      message.error(`删除失败：${error.message || '未知错误'}`);
      return false;
    }
  };

  // 处理创建文件夹
  const handleCreateFolder = async (name: string, parentId?: number) => {
    const normalizedName = name.trim();
    const nameValidationError = validateResourceName(normalizedName);
    if (nameValidationError) {
      message.warning(nameValidationError);
      throw new Error(nameValidationError);
    }

    try {
      const result = await ResourceHelpers.createFolder(
        normalizedName,
        parentId
      );
      await onReload();
      return result;
    } catch (error: any) {
      console.error('创建文件夹失败:', error);
      message.error(`创建文件夹失败：${error.message || '未知错误'}`);
      throw error;
    }
  };

  // 处理创建本体
  const handleCreateOntology = async (name: string, parentId?: number) => {
    const normalizedName = name.trim();
    const nameValidationError = validateResourceName(normalizedName);
    if (nameValidationError) {
      message.warning(nameValidationError);
      throw new Error(nameValidationError);
    }

    try {
      const result = await ResourceHelpers.createOntology(
        normalizedName,
        parentId
      );
      await onReload();
      return result;
    } catch (error: any) {
      console.error('创建本体失败:', error);
      message.error(`创建本体失败：${error.message || '未知错误'}`);
      throw error;
    }
  };

  // 创建操作菜单（根据权限过滤菜单项）
  const createActionMenu = (nodeData: OntologyNode) => {
    const canEdit = nodeData.canEdit ?? true;
    // const canManage = nodeData.canManage ?? true;

    const items: any[] = [];

    if (canEdit) {
      items.push(
        // 重命名功能已移至基础信息页面，此处隐藏
        // {
        //   key: 'rename',
        //   label: (
        //     <span className="flex items-center">
        //       <EditOutlined className="mr-2" />
        //       重命名
        //     </span>
        //   ),
        //   onClick: () => onShowRenameModal(nodeData),
        // },
        {
          key: 'move',
          label: (
            <span className="flex items-center">
              <DragOutlined className="mr-2" />
              移动到
            </span>
          ),
          onClick: () => onShowMoveModal(nodeData),
        },
        {
          key: 'delete',
          label: (
            <span className="flex items-center text-red-500">
              <DeleteOutlined className="mr-2" />
              删除
            </span>
          ),
          onClick: () => onShowDeleteModal(nodeData),
        }
      );
    }

    // 非默认文件夹且有管理权限才可删除
    // if (canManage && nodeData.title !== 'Default') {
    //   items.push({
    //     key: 'delete',
    //     label: (
    //       <span className="flex items-center text-red-500">
    //         <DeleteOutlined className="mr-2" />
    //         删除
    //       </span>
    //     ),
    //     onClick: () => onShowDeleteModal(nodeData),
    //   });
    // }

    return { items };
  };

  // 名称过长时截断显示，title 属性保留完整名称便于悬停查看
  const getDisplayTitle = (title: string) => {
    if (!title) {
      return '';
    }
    return title.length > RESOURCE_TITLE_DISPLAY_MAX_LENGTH
      ? `${title.slice(0, RESOURCE_TITLE_DISPLAY_MAX_LENGTH)}...`
      : title;
  };

  // 转换数据为 Tree 组件需要的格式
  const convertToTreeData = (data: OntologyNode[]): any[] => {
    return data.map(item => {
      if (item.isEditing && editingKey === item.key) {
        const isFolder = item.type === 'folder';
        const placeholderText = isFolder ? '输入文件夹名称' : '输入本体名称';
        const iconComponent = isFolder ? <FolderIcon /> : <OntologyIcon />;

        return {
          key: item.key,
          title: (
            <Input
              size="small"
              defaultValue={editingValue}
              maxLength={RESOURCE_NAME_MAX_LENGTH}
              onChange={e => {
                e.stopPropagation();
                editingValueRef.current = e.target.value;
                if (!isComposingRef.current) {
                  setEditingValue(e.target.value);
                }
              }}
              onPressEnter={e => {
                e.stopPropagation();
                if (
                  isComposingRef.current ||
                  (e.nativeEvent as any).isComposing
                ) {
                  return;
                }
                handleConfirmEdit(item.key, editingValueRef.current);
              }}
              onBlur={e => {
                e.stopPropagation();
                if (isComposingRef.current) {
                  return;
                }
                setTimeout(() => {
                  handleConfirmEdit(item.key, editingValueRef.current);
                }, 100);
              }}
              onCompositionStart={e => {
                e.stopPropagation();
                isComposingRef.current = true;
              }}
              onCompositionEnd={e => {
                e.stopPropagation();
                isComposingRef.current = false;
                // 组合输入结束后同步最终值，避免中文输入被截断
                editingValueRef.current = e.currentTarget.value;
                setEditingValue(e.currentTarget.value);
              }}
              onKeyDown={e => {
                e.stopPropagation();
                if (
                  isComposingRef.current ||
                  (e.nativeEvent as any).isComposing
                ) {
                  return;
                }
                if (e.key === 'Escape') {
                  handleCancelEdit(item.key);
                }
              }}
              onClick={e => e.stopPropagation()}
              onFocus={e => e.stopPropagation()}
              autoFocus
              placeholder={placeholderText}
              className="!border-blue-500 focus:!border-blue-500 focus:!shadow-blue-200"
              style={{ width: '140px', marginLeft: '0px' }}
            />
          ),
          icon: iconComponent,
          children: item.children
            ? convertToTreeData(item.children)
            : undefined,
          isLeaf: !isFolder,
          data: item,
          className: 'ant-tree-treenode-editing',
        };
      }

      const displayTitle = getDisplayTitle(item.title);

      return {
        key: item.key,
        title: (
          <div className="flex items-center justify-between group">
            <span title={item.title}>{displayTitle}</span>
            <Dropdown
              menu={createActionMenu(item)}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={e => e.stopPropagation()}
              />
            </Dropdown>
          </div>
        ),
        icon: item.type === 'folder' ? <FolderIcon /> : <OntologyIcon />,
        children: item.children ? convertToTreeData(item.children) : undefined,
        isLeaf: item.type === 'ontology',
        data: item,
        disabled: item.isEditing, // 编辑状态下禁用拖拽
        className: `resource-tree-node ${
          item.title === 'Default' ? 'default-folder' : ''
        } ${item.isEditing ? 'editing-node' : ''}`,
      };
    });
  };

  // 搜索过滤：保留命中的节点与其路径，并自动展开命中路径
  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const isSearching = normalizedSearchValue.length > 0;
  const filterTreeData = (
    nodes: OntologyNode[],
    keyword: string
  ): { filtered: OntologyNode[]; expandedKeys: React.Key[] } => {
    const expandedKeySet = new Set<React.Key>();
    const filterNodes = (nodeList: OntologyNode[]): OntologyNode[] => {
      return nodeList.reduce((acc, node) => {
        const titleValue = node.title || '';
        const isMatch = titleValue.toLowerCase().includes(keyword);
        const isEditingNode = node.isEditing && editingKey === node.key;
        const filteredChildren = node.children
          ? filterNodes(node.children)
          : [];

        if (filteredChildren.length > 0) {
          expandedKeySet.add(node.key);
        }

        if (isMatch || isEditingNode || filteredChildren.length > 0) {
          acc.push({
            ...node,
            children:
              filteredChildren.length > 0 ? filteredChildren : undefined,
          });
        }

        return acc;
      }, [] as OntologyNode[]);
    };

    return {
      filtered: filterNodes(nodes),
      expandedKeys: Array.from(expandedKeySet),
    };
  };

  const filteredResult = isSearching
    ? filterTreeData(ontologyList, normalizedSearchValue)
    : { filtered: ontologyList, expandedKeys: [] as React.Key[] };
  const displayExpandedKeys = isSearching
    ? Array.from(new Set([...expandedKeys, ...filteredResult.expandedKeys]))
    : expandedKeys;

  const treeData = convertToTreeData(filteredResult.filtered);

  const { shouldHideHeader } = useEmbedMode();

  return (
    <div
      className="w-50 h-full min-h-0 overflow-hidden border-r border-gray-200 flex flex-col"
      style={{ maxHeight: shouldHideHeader ? '100vh' : 'calc(100vh - 128px)' }}
    >
      {/* 添加拖拽样式 */}
      <style>{`
        .resource-tree-node.editing-node {
          opacity: 0.8;
          pointer-events: none;
        }
        .ant-tree-node-content-wrapper:hover {
          background-color: #f5f5f5;
        }
        .ant-tree-draggable-icon {
          display: none !important;
        }
        .ant-tree-drag-over {
          background-color: #e6f7ff;
        }
        .ant-tree-drag-over-gap-top::before {
          border-top: 2px solid #1890ff;
        }
        .ant-tree-drag-over-gap-bottom::before {
          border-bottom: 2px solid #1890ff;
        }
        .delete-drop-zone {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 120px;
          height: 80px;
          background: linear-gradient(135deg, #ff4d4f, #ff7875);
          border: 2px dashed #fff;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(255, 77, 79, 0.3);
          transition: all 0.3s ease;
          z-index: 1000;
          opacity: 0;
          pointer-events: none;
          transform: translateY(10px);
        }
        .delete-drop-zone.visible {
          opacity: 1;
          pointer-events: auto;
          transform: translateY(0);
        }
        .delete-drop-zone.drag-over {
          transform: scale(1.1) translateY(0);
          box-shadow: 0 6px 20px rgba(255, 77, 79, 0.5);
          background: linear-gradient(135deg, #ff1f23, #ff4d4f);
        }
        .delete-drop-zone .delete-icon {
          font-size: 24px;
          margin-bottom: 4px;
        }
      `}</style>
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-end justify-between mb-3">
          <Title level={4} className="!mb-0">
            仓库
          </Title>
          <div className="flex space-x-2">
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAddFolder}
              className="!w-6 !h-6 !min-w-6 !p-0 flex items-center justify-center hover:!bg-blue-50 hover:!text-blue-500 !border-gray-300"
              title="添加文件夹"
              disabled={loading}
            />
            <Button
              type="text"
              size="small"
              icon={<FileTextOutlined />}
              onClick={handleAddOntology}
              className="!w-6 !h-6 !min-w-6 !p-0 flex items-center justify-center hover:!bg-blue-50 hover:!text-blue-500 !border-gray-300"
              title="新建本体"
              disabled={loading}
            />
          </div>
        </div>

        {/* 拖拽提示 */}
        <div className="text-xs text-gray-400 mb-2 px-1">
          支持拖拽移动文件夹和本体，可拖拽到右下角删除
        </div>
        <Input
          placeholder="搜索"
          prefix={<SearchOutlined className="text-gray-400" />}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          className="!border-gray-300"
          disabled={loading}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spin size="large" />
            <Text className="ml-2 text-gray-500">加载中...</Text>
          </div>
        ) : (
          <Tree
            showIcon
            defaultExpandAll
            blockNode
            draggable
            allowDrop={allowDrop}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDrop={handleDrop}
            expandedKeys={displayExpandedKeys}
            onExpand={keys => setExpandedKeys && setExpandedKeys(keys)}
            treeData={treeData}
            selectedKeys={selectedKeys}
            onSelect={onSelect}
          />
        )}
      </div>

      {/* 删除区域 */}
      <div
        className={`delete-drop-zone ${isDragging ? 'visible' : ''} ${dragOverDelete ? 'drag-over' : ''}`}
        onDrop={handleDeleteDrop}
        onDragOver={e => {
          e.preventDefault();
          setDragOverDelete(true);
        }}
        onDragLeave={e => {
          e.preventDefault();
          setDragOverDelete(false);
        }}
      >
        <DeleteOutlined className="delete-icon" />
        <span>拖拽删除</span>
      </div>

      {/* 拖拽时显示删除区域的全局样式 */}
      <style>{`
        .ant-tree .ant-tree-treenode-draggable .ant-tree-node-content-wrapper {
          cursor: grab;
        }
        .ant-tree .ant-tree-treenode-draggable .ant-tree-node-content-wrapper:active {
          cursor: grabbing;
        }
      `}</style>
    </div>
  );
};
